import { useEffect, useState } from 'react';

import { App, Divider, Form, InputNumber, Modal } from 'antd';
import { useTranslation } from 'react-i18next';

import { ApiErrorData } from 'models/ApiResponse';
import { QuizSettings, UpdateQuizSettingsInput } from 'models/Quiz';

import { QuizService } from 'services/QuizService';

import appI18n from '../../../config/i18n';

interface Props {
  isOpen: boolean;
  handleModalCancel: () => void;
  onSaved?: () => void;
}

interface FormValues {
  inductionMaxAttempts: number;
  maxVideoSizeMb: number;
  videoLinkMinutes: number;
}

function QuizSettingsModal({ isOpen, handleModalCancel, onSaved }: Props) {
  const { t } = useTranslation(undefined, { i18n: appI18n });
  const { message } = App.useApp();
  const [form] = Form.useForm<FormValues>();

  const [loading, setLoading] = useState<boolean>(false);
  const [submitting, setSubmitting] = useState<boolean>(false);

  useEffect(() => {
    if (!isOpen) return;
    form.resetFields();
    fetchSettings();
  }, [isOpen]);

  const fetchSettings = async () => {
    setLoading(true);
    const response = await QuizService.getQuizSettings();
    if (response.success) {
      const data = response.data as QuizSettings;
      form.setFieldsValue({
        inductionMaxAttempts: data.inductionMaxAttempts,
        maxVideoSizeMb: data.maxVideoSizeMb,
        videoLinkMinutes: data.videoLinkMinutes,
      });
    } else {
      const error = response.data as ApiErrorData;
      message.error(
        (error.message as string) ||
          t('scenes.quizzes.scene.settings.errors.fetch'),
        3.5
      );
    }
    setLoading(false);
  };

  const handleSubmit = async () => {
    let values: FormValues;
    try {
      values = await form.validateFields();
    } catch (e) {
      return;
    }

    const body: UpdateQuizSettingsInput = {
      inductionMaxAttempts: values.inductionMaxAttempts,
      maxVideoSizeMb: values.maxVideoSizeMb,
      videoLinkMinutes: values.videoLinkMinutes,
    };

    setSubmitting(true);
    const response = await QuizService.updateQuizSettings(body);
    setSubmitting(false);

    if (response.success) {
      message.success(t('scenes.quizzes.scene.settings.success'), 3);
      handleModalCancel();
      onSaved?.();
    } else {
      const error = response.data as ApiErrorData;
      message.error(
        (error.message as string) ||
          t('scenes.quizzes.scene.settings.errors.update_failed'),
        3.5
      );
    }
  };

  return (
    <Modal
      title={t('scenes.quizzes.scene.settings.title')}
      open={isOpen}
      onCancel={handleModalCancel}
      onOk={handleSubmit}
      okText={t('scenes.quizzes.scene.settings.buttons.save')}
      cancelText={t('scenes.quizzes.scene.settings.buttons.cancel')}
      confirmLoading={submitting}
      destroyOnClose
      width={460}
      maskClosable={false}
    >
      <p style={{ marginTop: 4, color: 'rgba(0, 0, 0, 0.45)' }}>
        {t('scenes.quizzes.scene.settings.subtitle')}
      </p>
      <Divider style={{ marginTop: '12px', marginBottom: '15px' }} />
      <Form form={form} layout="vertical" preserve={false} disabled={loading}>
        <Form.Item
          name="inductionMaxAttempts"
          label={t(
            'scenes.quizzes.scene.settings.fields.induction_max_attempts'
          )}
          tooltip={t(
            'scenes.quizzes.scene.settings.hints.induction_max_attempts'
          )}
          rules={[
            {
              required: true,
              message: t(
                'scenes.quizzes.scene.settings.validations.induction_max_attempts'
              ),
            },
            {
              type: 'integer',
              min: 0,
              max: 10,
              message: t(
                'scenes.quizzes.scene.settings.validations.induction_max_attempts'
              ),
            },
          ]}
        >
          <InputNumber min={0} max={10} style={{ width: '100%' }} />
        </Form.Item>

        <Form.Item
          name="maxVideoSizeMb"
          label={t('scenes.quizzes.scene.settings.fields.max_video_size')}
          rules={[
            {
              required: true,
              message: t(
                'scenes.quizzes.scene.settings.validations.max_video_size'
              ),
            },
            {
              type: 'integer',
              min: 1,
              max: 1024,
              message: t(
                'scenes.quizzes.scene.settings.validations.max_video_size'
              ),
            },
          ]}
        >
          <InputNumber
            min={1}
            max={1024}
            addonAfter={t('scenes.quizzes.scene.settings.units.mb')}
            style={{ width: '100%' }}
          />
        </Form.Item>

        <Form.Item
          name="videoLinkMinutes"
          label={t('scenes.quizzes.scene.settings.fields.video_link_minutes')}
          tooltip={t('scenes.quizzes.scene.settings.hints.video_link_minutes')}
          rules={[
            {
              required: true,
              message: t(
                'scenes.quizzes.scene.settings.validations.video_link_minutes'
              ),
            },
            {
              type: 'integer',
              min: 1,
              max: 300,
              message: t(
                'scenes.quizzes.scene.settings.validations.video_link_minutes'
              ),
            },
          ]}
        >
          <InputNumber
            min={1}
            max={300}
            addonAfter={t('scenes.quizzes.scene.settings.units.minutes')}
            style={{ width: '100%' }}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
}

export default QuizSettingsModal;
